// src/hooks/useVoiceInput.js
import { useState, useRef, useCallback } from "react";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const CATEGORY_ALIASES = {
  "billing": "billing", "ar": "billing", "claims": "billing",
  "credentialing": "credentialing", "credentials": "credentialing", "license": "credentialing",
  "hr": "hr", "payroll": "hr", "human resources": "hr",
  "onboarding": "onboarding", "training": "onboarding",
  "compliance": "compliance", "hipaa": "compliance",
  "vendors": "vendors", "bills": "vendors", "rent": "vendors",
  "operations": "pyha_ops", "pyha ops": "pyha_ops",
  "hockey finance": "pyha_finance", "pyha finance": "pyha_finance",
  "hockey legal": "pyha_legal", "pyha legal": "pyha_legal",
  "programs": "pyha_programs", "ice": "pyha_programs",
  "general": "general",
};

const RECURRENCE_ALIASES = {
  "weekly": "weekly", "every week": "weekly",
  "biweekly": "biweekly", "every two weeks": "biweekly", "every 2 weeks": "biweekly",
  "monthly": "monthly", "every month": "monthly",
  "quarterly": "quarterly", "every quarter": "quarterly",
  "annually": "annually", "yearly": "annually", "every year": "annually",
};

function parseRelativeDate(text) {
  const lower = text.toLowerCase();
  const today = new Date(); today.setHours(0,0,0,0);
  if (lower.includes("today")) return toStr(today);
  if (lower.includes("tomorrow")) { today.setDate(today.getDate()+1); return toStr(today); }
  const days = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
  for (let i = 0; i < days.length; i++) {
    if (lower.includes(days[i])) {
      const d = new Date(today);
      d.setDate(d.getDate() + ((i - d.getDay() + 7) % 7 || 7));
      return toStr(d);
    }
  }
  const m = lower.match(/in (\d+) (day|days|week|weeks|month|months)/);
  if (m) {
    const n = parseInt(m[1]), unit = m[2], d = new Date(today);
    if (unit.startsWith("day")) d.setDate(d.getDate()+n);
    else if (unit.startsWith("week")) d.setDate(d.getDate()+n*7);
    else d.setMonth(d.getMonth()+n);
    return toStr(d);
  }
  if (lower.includes("end of month")) return toStr(new Date(today.getFullYear(), today.getMonth()+1, 0));
  return null;
}

function toStr(d) { return d.toISOString().split("T")[0]; }

function parseVoice(transcript) {
  let text = transcript.toLowerCase().replace(/^(add item[:\-]?|remind me to|add[:\-]?|new item[:\-]?)/i,"").trim();
  const result = { title:"", category:"", due:"", recurrence:"none", notes:"" };
  for (const [alias,val] of Object.entries(RECURRENCE_ALIASES)) {
    if (text.includes(alias)) { result.recurrence=val; text=text.replace(alias,"").trim(); break; }
  }
  const datePat = /(due )?(today|tomorrow|next \w+day|in \d+ \w+|end of month)/i;
  const dm = text.match(datePat);
  if (dm) { const p=parseRelativeDate(dm[0]); if(p){ result.due=p; text=text.replace(dm[0],"").trim(); } }
  for (const [alias,cat] of Object.entries(CATEGORY_ALIASES)) {
    if (text.includes(alias)) { result.category=cat; text=text.replace(alias,"").trim(); break; }
  }
  result.title = text.replace(/,\s*,/g,",").replace(/^[,\s]+|[,\s]+$/g,"").replace(/\s+/g," ").trim();
  if (result.title) result.title = result.title[0].toUpperCase()+result.title.slice(1);
  return result;
}

export function useVoiceInput({ onResult, onTranscript }) {
  const [listening, setListening] = useState(false);
  const [supported] = useState(() => !!SpeechRecognition);
  const ref = useRef(null);

  const start = useCallback(() => {
    if (!SpeechRecognition) return;
    const r = new SpeechRecognition();
    r.continuous = false; r.interimResults = true; r.lang = "en-US";
    ref.current = r;
    r.onstart = () => setListening(true);
    r.onend = () => setListening(false);
    r.onresult = (e) => {
      const t = Array.from(e.results).map(x=>x[0].transcript).join("");
      onTranscript?.(t);
      if (e.results[0].isFinal) onResult?.(parseVoice(t), t);
    };
    r.onerror = () => setListening(false);
    r.start();
  }, [onResult, onTranscript]);

  const stop = useCallback(() => { ref.current?.stop(); setListening(false); }, []);
  return { listening, supported, startListening: start, stopListening: stop };
}
